import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../../../config/queryKeys'
import {
  getScheduleTeams,
  getSubCompetitions,
  getVenueFacilities,
  getVenues,
  type FacilityOption,
  type SubCompetitionLevel,
  type TeamOption,
  type VenueOption,
} from '../../../../services/game/gameOptionsService'

/**
 * Location options.
 *
 * `recursive` is part of the key, not just the request: the "All Locations" /
 * "Within <organization>" toggle changes what comes back, so the two lists must
 * not share a cache entry.
 */
export const useVenues = (
  scheduleId?: string | null,
  recursive = true,
  enabled = true
) => {
  const query = useQuery<VenueOption[]>({
    queryKey: queryKeys.venues.bySchedule(scheduleId, recursive),
    queryFn: () => getVenues(scheduleId, recursive),
    enabled,
  })

  return { ...query, venues: query.data ?? [] }
}

/** Facilities for the selected location. Idle until a venue is chosen. */
export const useVenueFacilities = (venueId?: string | null) => {
  const query = useQuery<FacilityOption[]>({
    queryKey: queryKeys.venueFacilities.byVenue(venueId),
    queryFn: () => getVenueFacilities(venueId!),
    enabled: Boolean(venueId),
  })

  return { ...query, facilities: query.data ?? [] }
}

/** Home/visiting team options for the parent competition. */
export const useScheduleTeams = (scheduleId?: string | null) => {
  const query = useQuery<TeamOption[]>({
    queryKey: queryKeys.scheduleTeams.bySchedule(scheduleId),
    queryFn: () => getScheduleTeams(scheduleId!),
    enabled: Boolean(scheduleId),
    /*
     * Well below the 5-minute global default. Teams are created from other
     * screens, and `TeamModal` invalidates this key when they are -- but that
     * only covers creations made in THIS tab. A short window keeps a team added
     * elsewhere from being missing for minutes, at the cost of one cheap refetch.
     */
    staleTime: 20_000,
  })

  return { ...query, teams: query.data ?? [] }
}

/**
 * The competition's divisions (or pools, or whatever the level is called).
 *
 * Idle until `enabled` — only a placeholder team makes this question worth
 * asking, and every other game already knows its division.
 */
export const useSubCompetitions = (
  scheduleId?: string | null,
  enabled = true
) => {
  const query = useQuery<SubCompetitionLevel>({
    queryKey: queryKeys.subCompetitions.bySchedule(scheduleId),
    queryFn: () => getSubCompetitions(scheduleId!),
    enabled: Boolean(scheduleId) && enabled,
  })

  return {
    ...query,
    subCompetitions: query.data?.schedules ?? [],
    /** The level's own name, used as the field label. */
    subCompetitionLabel: query.data?.name ?? '',
  }
}
